import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useCheckoutMode } from "@/context/CheckoutModeContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetTrigger, SheetContent, SheetClose, SheetTitle } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import { adminEntry } from "@/lib/adminHost";
import { isPartnerHost, partnerEntryFor } from "@/lib/partnerHost";
import "@/styles/landing-theme.css";
import { Bed, AirplaneTilt, Package, Car, MapPin, Taxi, List, User, SignOut, LockKey, Ticket, Lifebuoy, Storefront, UsersThree, CaretDown } from "@phosphor-icons/react";
import LocalePicker, { LocaleChoices } from "@/components/layout/LocalePicker";
import { cn } from "@/lib/utils";
import { directionOf, languageName, pick } from "@/lib/languages";
import { useRecordSearches } from "@/lib/recentSearches";
import { useScrollRow } from "@/lib/scrollRow";
import { chromeL } from "@/i18n/chrome";
import { isNativeApp } from "@/lib/nativeApp";
import AppHeader from "@/components/app/AppHeader";

const NAV_ITEMS = [
  { to: "/", key: "stays", icon: Bed, end: true },
  { to: "/flights", key: "flights", icon: AirplaneTilt },
  { to: "/flight-hotel", key: "flightHotel", icon: Package },
  { to: "/cars", key: "cars", icon: Car },
  { to: "/attractions", key: "attractions", icon: MapPin },
  { to: "/airport-taxis", key: "taxis", icon: Taxi },
];
// Trip Groupe is not among these: it lives in the menus, "Voyages de groupe"
// (its presentation for a visitor, the groups for a member), and in the
// footer (owner, 2026-10-05: "enlever Trip Groupe ... et remettre seulement
// sur le menu").

// Routes that render search results. The landing pages share these paths, so
// the query string is what separates "browsing" from "searching".
const SEARCH_ROUTES = new Set(["/flights", "/hotels", "/cars", "/flight-hotel"]);

// One row style for the phone menu's links; the current page is marked by
// aria-current (NavLink) and drawn as a pale row with an ink bar and bold text.
const MENU_ROW = "relative flex min-h-12 items-center gap-3 rounded-md px-3 text-body-md text-touki-black transition-colors duration-fast hover:bg-touki-light";
const MENU_CURRENT = "bg-touki-light font-bold before:absolute before:inset-y-2 before:start-0 before:w-1 before:rounded-full before:bg-touki-dark before:content-['']";

// The profile is Toukky's night blue (owner, 2026-10-10: "les noms de profil
// en haut soient en bleu"): the initial on the blue, never the colours of a
// Google picture.
function Avatar({ user, size = "h-8 w-8 text-xs" }) {
  return (
    <span className={cn("inline-flex items-center justify-center rounded-full bg-touki-dark font-bold text-white", size)} data-testid="profile-avatar">
      {(user.name || user.email).slice(0, 1).toUpperCase()}
    </span>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, siteLang = lang, currency, t } = useSettings();
  const c = pick(chromeL, lang);
  // The menu's "Langue et devise" line, open or closed.
  const [localeOpen, setLocaleOpen] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const categoryRow = useRef(null);
  // The visitor's own recent searches (Home's "Pick up where you left off"):
  // a results page opened anywhere is remembered on this device.
  useRecordSearches(location.pathname, location.search);
  // On a phone the product row scrolls: keep the current product in view.
  useScrollRow(categoryRow, location.pathname);
  // /booking/:id is a checkout before payment and a confirmation after it.
  // The page that fetched the booking is the only thing that knows which, so
  // the path alone is not enough to decide whether to strip the header.
  const { isCheckout } = useCheckoutMode();
  // A booking's payment, and a stay's one page that books and pays (pages/checkout/StayBooking).
  const checkoutMode = isCheckout && /^\/(booking\/[^/]+|hotels\/[^/]+\/book)$/.test(location.pathname);

  // Once a search has run, the product row costs a line of a small screen to
  // offer the one thing the traveller has just stopped doing. Booking.com drops
  // it on mobile results for the same reason and keeps it on desktop, where the
  // row is free. Nothing is lost either way: the menu still lists every product.
  //
  // Keyed on the query string, not the path, so the flights and stays landing
  // pages keep their tabs — it is the search that changes the intent, not the
  // route.
  const searchResults =
    SEARCH_ROUTES.has(location.pathname) && location.search.length > 1;
  // Only Home carries the six-service tab row in its hero; every service page
  // shows its own form alone and reaches the others through this header row.
  const compactOnPhone = searchResults;
  const onStays = location.pathname === "/hotels" || location.pathname.startsWith("/hotels/");
  // The phone menu's links sit in SheetClose (a Radix Slot), which turns a
  // className function into text: the current page is worked out here, the
  // way NavLink does (an exact match with `end`, the path or below it without).
  const current = (to, end) => location.pathname === to || (!end && location.pathname.startsWith(`${to}/`));
  // Group trips: a member's own groups; a visitor without an account, or a
  // member without a group (GroupList sends them on), meets the presentation
  // (pages/tripgroupe), whose button leads on to them.
  const groupsHome = user ? "/groups" : "/trip-groupe";
  const onGroups = current(groupsHome) || current("/trip-groupe");
  const signOut = async () => {
    const confirmed = await logout();
    if (confirmed === null) return;
    if (confirmed === false) toast.warning(c.logoutUnconfirmed);
    nav("/");
  };

  // The traveller app draws its own top of screen: a compact row on Accueil,
  // back and a title elsewhere (owner, 2026-10-24); the website keeps this one.
  if (isNativeApp()) return <AppHeader />;

  if (checkoutMode) {
    return (
      <header className="sticky top-0 z-header border-b border-touki bg-white" data-testid="site-header">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-16 lg:px-8">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <Logo size={30} wordmarkClass="text-xl text-touki-black" />
          </Link>
          <div className="inline-flex items-center gap-2 text-label-lg font-semibold text-touki-black" data-testid="secure-checkout-label">
            <LockKey size={18} weight="bold" aria-hidden="true" />
            <span>{c.secureCheckout}</span>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="landing-header sticky top-0 z-header border-b border-touki bg-white text-touki-black" data-testid="site-header">
      {/* One row at every width: the logo, then the account. From 1280px the
          products sit between them; below that they drop to their own row, on
          every page, Home too (owner, 2026-10-04: from Séjours to Vols the row
          stays where it is). */}
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-2 px-4 sm:px-6 lg:px-8 xl:flex-nowrap xl:gap-x-6">
        <Link to="/" className="order-1 flex h-14 shrink-0 items-center lg:h-16" data-testid="logo-link">
          <Logo size={30} wordmarkClass="text-xl text-touki-black" />
        </Link>

        <div className="order-2 flex shrink-0 items-center gap-1 sm:gap-2 xl:order-3">
          {/* Language and currency: the full pill on a wide screen; on a phone or a
              tablet they are in the menu (owner, 2026-10-30, after the 10-24 chip). */}
          <LocalePicker className="hidden xl:inline-flex" />

          <a href={partnerEntryFor()} className="hidden h-11 items-center whitespace-nowrap rounded-md px-3 text-label-lg font-semibold text-touki-black transition-colors duration-fast hover:bg-touki-light xl:inline-flex" data-testid="list-property-link">
            {t("nav.listProperty")}
          </a>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex h-11 items-center gap-2 rounded-full border border-touki pe-1 ps-1 text-touki-black transition-colors duration-fast hover:bg-touki-light sm:pe-3" data-testid="user-menu-trigger">
                  <Avatar user={user} />
                  <span className="hidden max-w-[120px] truncate text-label-lg font-semibold text-touki-dark sm:inline" data-testid="profile-name">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account"><User className="me-2" size={16} aria-hidden="true" /> {t("nav.account")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/bookings")} data-testid="menu-bookings"><Ticket className="me-2" size={16} aria-hidden="true" /> {t("nav.bookings")}</DropdownMenuItem>
                {!isPartnerHost() && <DropdownMenuItem onClick={() => nav("/groups")} data-testid="menu-groups"><UsersThree className="me-2" size={16} aria-hidden="true" /> {t("nav.groupTrips")}</DropdownMenuItem>}
                {user.role === "admin" && <DropdownMenuItem onClick={() => { window.location.assign(adminEntry()); }} data-testid="menu-admin">{t("nav.admin")}</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} data-testid="menu-logout"><SignOut className="me-2" size={16} aria-hidden="true" /> {t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-1">
              <Button variant="ghost" className="hidden h-11 px-3 text-label-lg md:inline-flex xl:hidden" onClick={() => nav("/manage")} data-testid="header-manage-btn">{t("nav.manageBooking")}</Button>
              <Button variant="ghost" className="hidden h-11 px-3 text-label-lg sm:inline-flex" onClick={() => nav("/signup")} data-testid="header-signup-btn">{t("nav.signup")}</Button>
              <Button variant="ink" className="hidden h-11 bg-touki-dark px-4 text-label-lg text-white sm:inline-flex" onClick={() => nav("/login")} data-testid="header-login-btn">{t("nav.login")}</Button>
              {/* On a phone the word gives way to the person, as Booking.com does. */}
              <button type="button" onClick={() => nav("/login")} data-testid="header-login-icon" aria-label={t("nav.login")} title={t("nav.login")}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-touki-black transition-colors duration-fast hover:bg-touki-light sm:hidden">
                <User size={22} weight="bold" aria-hidden="true" />
              </button>
            </div>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-touki-black transition-colors duration-fast hover:bg-touki-light xl:hidden" data-testid="mobile-menu-trigger" aria-label={c.menu}>
                <List size={22} weight="bold" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" aria-describedby={undefined} className="flex w-[min(340px,100vw)] flex-col p-0" data-testid="mobile-menu-sheet">
              <SheetTitle className="sr-only">{c.menu}</SheetTitle>
              <div className="flex h-14 shrink-0 items-center border-b border-touki px-4">
                <Logo size={26} wordmarkClass="text-lg text-touki-black" />
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-4">
                {/* The products are not here: the home screen and the row under the
                    header already offer them (owner, 2026-10-30). */}
                <nav aria-labelledby="menu-trip-title">
                  <p id="menu-trip-title" className="mb-1 px-3 text-label-md font-semibold text-touki-muted">{t("nav.menuTrip")}</p>
                  <ul className="flex flex-col gap-0.5">
                    <li>
                      <SheetClose asChild>
                        <NavLink to="/manage" data-testid="mobile-manage-booking-link" className={cn(MENU_ROW, current("/manage") && MENU_CURRENT)}>
                          <Ticket size={20} weight="bold" aria-hidden="true" className="shrink-0" />{t("nav.manageBooking")}
                        </NavLink>
                      </SheetClose>
                    </li>
                    {!isPartnerHost() && (
                      <li>
                        <SheetClose asChild>
                          {/* A Link, not a NavLink: the presentation is this entry's page too, for a member. */}
                          <Link to={groupsHome} data-testid="mobile-groups-link" aria-current={onGroups ? "page" : undefined} className={cn(MENU_ROW, onGroups && MENU_CURRENT)}>
                            <UsersThree size={20} weight="bold" aria-hidden="true" className="shrink-0" />{t("nav.groupTrips")}
                          </Link>
                        </SheetClose>
                      </li>
                    )}
                    <li>
                      <SheetClose asChild>
                        <NavLink to="/support" data-testid="mobile-support-link" className={cn(MENU_ROW, current("/support") && MENU_CURRENT)}>
                          <Lifebuoy size={20} weight="bold" aria-hidden="true" className="shrink-0" />{t("nav.support")}
                        </NavLink>
                      </SheetClose>
                    </li>
                    <li>
                      <SheetClose asChild>
                        <a href={partnerEntryFor()} data-testid="mobile-list-property-link" className={MENU_ROW}>
                          <Storefront size={20} weight="bold" aria-hidden="true" className="shrink-0" />{t("nav.listProperty")}
                        </a>
                      </SheetClose>
                    </li>
                  </ul>
                </nav>

                <section aria-labelledby="menu-prefs-title" className="mt-5 px-3">
                  <p id="menu-prefs-title" className="mb-2 text-label-md font-semibold text-touki-muted">{t("nav.menuPrefs")}</p>
                  {/* One line says the language and the currency in use; tapped, it
                      opens the languages and the currencies (the header panel's
                      lists), each its own choice, the one in use ticked. */}
                  <button type="button" aria-expanded={localeOpen} aria-controls="menu-locale-choices" onClick={() => setLocaleOpen((open) => !open)}
                    data-testid="mobile-locale-toggle"
                    className="flex min-h-14 w-full items-center justify-between gap-3 rounded-md border border-touki px-3 py-2 text-start transition-colors duration-fast hover:bg-touki-light">
                    <span className="min-w-0">
                      <span className="block text-label-md text-touki-muted">{c.locale.open}</span>
                      <span className="block truncate text-body-sm font-semibold text-touki-black" data-testid="mobile-locale-summary">
                        <bdi dir={directionOf(siteLang)}>{languageName(siteLang)}</bdi>
                        <span aria-hidden="true" className="mx-1.5 text-touki-muted">·</span>
                        <span translate="no" className="tabular-nums">{currency}</span>
                      </span>
                    </span>
                    <CaretDown size={18} weight="bold" aria-hidden="true" className={cn("shrink-0 text-touki-black transition-transform duration-fast", localeOpen && "rotate-180")} />
                  </button>
                  {localeOpen && (
                    <div id="menu-locale-choices" className="mt-3" data-testid="mobile-locale-choices">
                      <LocaleChoices testPrefix="mobile-" />
                    </div>
                  )}
                </section>
              </div>

              <div className="shrink-0 border-t border-touki p-4">
                {user ? (
                  <div className="flex items-center gap-3">
                    <Avatar user={user} size="h-10 w-10 text-sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-label-lg font-semibold">{user.name}</p>
                      <SheetClose asChild>
                        <Link to="/bookings" className="inline-flex min-h-11 items-center text-label-md font-semibold text-touki-black underline decoration-touki-field underline-offset-4" data-testid="mobile-bookings-link">{t("nav.bookings")}</Link>
                      </SheetClose>
                    </div>
                    <button type="button" onClick={signOut} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-touki-black hover:bg-touki-light" data-testid="mobile-logout-btn" aria-label={t("nav.logout")}>
                      <SignOut size={20} weight="bold" aria-hidden="true" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <SheetClose asChild>
                      <Button variant="ink" className="flex-1 bg-touki-dark text-white" onClick={() => nav("/login")} data-testid="mobile-login-btn">{t("nav.login")}</Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button variant="outline" className="flex-1" onClick={() => nav("/signup")} data-testid="mobile-signup-btn">{t("nav.signup")}</Button>
                    </SheetClose>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* The products: the same row, at the same place, on every page. */}
        {/* The separator line stays on this frame; only the row inside it
            scrolls and fades at the edge that still hides links. */}
        <div
          data-testid="category-nav"
          className={`order-3 -mx-4 min-w-0 basis-full border-t border-touki bg-white sm:-mx-6 lg:-mx-8 xl:order-2 xl:mx-0 xl:flex-1 xl:basis-auto xl:border-t-0 ${compactOnPhone ? "hidden md:block xl:flex" : "xl:flex"}`}
        >
          <div ref={categoryRow} className="min-w-0 overflow-x-auto px-4 tk-scroll-row scrollbar-none sm:px-6 lg:px-8 xl:flex xl:flex-1 xl:justify-center xl:px-0">
          <nav aria-label={c.travelNav} className="flex h-12 min-w-max items-center gap-1 xl:h-16">
            {NAV_ITEMS.map(({ to, key, icon: Icon, end }) => (
              <NavLink
                key={key}
                to={to}
                end={end}
                data-testid={`nav-${key}`}
                data-category-active={key === "stays" && onStays ? "true" : undefined}
                className={({ isActive }) =>
                  `inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-label-lg font-semibold transition-colors duration-fast ${
                    isActive
                      ? "border-touki-dark bg-touki-dark text-white"
                      : "border-transparent text-touki-black hover:bg-touki-light"
                  }`
                }
              >
                <Icon size={16} weight="bold" className="xl:hidden" aria-hidden="true" /> {t(`nav.${key}`)}
              </NavLink>
            ))}
          </nav>
          </div>
        </div>
      </div>
    </header>
  );
}
